import { useMemo, useState } from "react";
import { Separator } from "@/components/ui/separator";
import Header from "./components/header";
import Invoices from "./components/invoices";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import useVessel from "@/store/use-vessel";
import {
  getFirstDayOfMonth,
  getLastDayOfMonth,
  parseDateParam,
} from "@/lib/utils";
import { subDays, addDays, formatDate } from "date-fns";
import { useSearchInvoices } from "@/services/queries/invoices";

const DATE_RANGE = 7;

const InvoicesMonthly = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const date = searchParams.get("date");
  const [isDateSelected, setIsDateSelected] = useState(false);
  const { selectedVessel } = useVessel();

  const currentDate = useMemo(() => parseDateParam(date), [date]);
  const firstDayOfMonth = useMemo(
    () => getFirstDayOfMonth(currentDate),
    [currentDate]
  );
  const lastDayOfMonth = useMemo(
    () => getLastDayOfMonth(currentDate),
    [currentDate]
  );
  const startDate = useMemo(
    () => subDays(firstDayOfMonth, DATE_RANGE),
    [firstDayOfMonth]
  );
  const endDate = useMemo(
    () => addDays(lastDayOfMonth, DATE_RANGE),
    [lastDayOfMonth]
  );

  const { data, isPending, isRefetching, refetch } = useSearchInvoices({
    startDate: formatDate(startDate, "yyyy-MM-dd"),
    endDate: formatDate(endDate, "yyyy-MM-dd"),
    vesselId: selectedVessel?.id,
  });

  const invoices = useMemo(() => {
    return data?.data.value.items || [];
  }, [data]);

  const filteredInvoices = useMemo(() => {
    const currentMonth = currentDate.getMonth();
    const formattedDate = formatDate(currentDate, "yyyy-MM-dd");

    return invoices.filter((invoice) => {
      const invoiceDate = new Date(invoice.issueDate);
      const isSameMonth = invoiceDate.getMonth() === currentMonth;

      return isDateSelected
        ? formatDate(invoiceDate, "yyyy-MM-dd") === formattedDate
        : isSameMonth;
    });
  }, [isDateSelected, invoices, currentDate]);

  const handleRefetch = () => {
    refetch();
  };

  return (
    <>
      <title>{t("title.monthly")}</title>
      <Header
        invoices={invoices}
        isDateSelected={isDateSelected}
        setIsDateSelected={setIsDateSelected}
      />
      <Separator className="my-5" />
      <Invoices
        invoices={filteredInvoices}
        isPending={isPending || isRefetching}
        refetch={handleRefetch}
      />
    </>
  );
};

export default InvoicesMonthly;
